import { computed, nextTick, onMounted, onUnmounted, ref, type ComputedRef, type Ref } from "vue";

/**
 * The open/active/dismiss half of a combobox, which is the part that has nothing to do with what is
 * being listed.
 *
 * It was written inside YearInput, where roughly two thirds of the file was this rather than
 * anything about years. Keeping the two apart means the year component reads as "a text field of
 * four digits over a list of years" and the fiddly parts — where the highlight goes, which keys do
 * what, when the panel closes — are stated once.
 *
 * The caller keeps ownership of its VALUE. This owns no text, emits nothing, and calls `onCommit`
 * when a choice is made, so a caller that wants to confirm a change before accepting it still can.
 */
export const useListboxNavigation = <T>(config: {
  /** The options as currently shown. Filtering belongs to the caller. */
  items: ComputedRef<readonly T[]> | Ref<readonly T[]>;
  /** Stable DOM id per option, used for `aria-activedescendant` and to scroll it into view. */
  optionId: (item: T) => string;
  onCommit: (item: T) => void;
  /** Called after the list closes for any reason, so a caller can drop state that only applies while open. */
  onClose?: () => void;
  isDisabled?: () => boolean;
  /** Which option to highlight on open. Defaults to none. */
  activeOnOpen?: () => number;
  /**
   * WAI-ARIA's select-only combobox rather than its editable one: the trigger is a button, so Enter
   * and Space have no text field to reach and belong to the list. They open it when it is closed and
   * activate the highlighted option when it is open. Leave false wherever the user is typing.
   */
  selectOnly?: boolean;
  /** A panel rendered outside `root` (teleported), so a pointerdown on it does not count as outside. */
  panel?: Ref<HTMLElement | null>;
}) => {
  /** Containment for the outside-dismiss check; put it on the component's outermost element. */
  const root = ref<HTMLElement | null>(null);
  const isOpen = ref<boolean>(false);
  const activeIndex = ref<number>(-1);

  /** An empty list leaves nothing for `aria-expanded` to point at, so it does not count as open. */
  const isListVisible = computed(() => isOpen.value && config.items.value.length > 0);

  const activeOptionId = computed(() => {
    const items = config.items.value;
    if (activeIndex.value < 0 || activeIndex.value >= items.length) return undefined;
    return config.optionId(items[activeIndex.value]);
  });

  const scrollActiveIntoView = async () => {
    await nextTick();
    if (!activeOptionId.value) return;
    const element = document.getElementById(activeOptionId.value);
    // Guarded rather than optional-chained: environments without layout (jsdom) have the element
    // but not the method.
    if (typeof element?.scrollIntoView === "function") element.scrollIntoView({ block: "nearest" });
  };

  const setActive = (index: number) => {
    activeIndex.value = index;
    void scrollActiveIntoView();
  };

  const open = () => {
    if (config.isDisabled?.() || isOpen.value) return;
    isOpen.value = true;
    setActive(config.activeOnOpen?.() ?? -1);
  };

  const close = () => {
    isOpen.value = false;
    activeIndex.value = -1;
    config.onClose?.();
  };

  const moveActive = (delta: number) => {
    const count = config.items.value.length;
    if (count === 0) return;
    const next = activeIndex.value + delta;
    setActive(next < 0 ? count - 1 : next >= count ? 0 : next);
  };

  /** The one door a choice leaves by, whether it came from the keyboard or a click on an option. */
  const commit = (item: T) => {
    close();
    config.onCommit(item);
  };

  const handleKeydown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        isOpen.value ? moveActive(1) : open();
        break;
      case "ArrowUp":
        event.preventDefault();
        isOpen.value ? moveActive(-1) : open();
        break;
      case "Home":
        if (!isOpen.value) return;
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        if (!isOpen.value) return;
        event.preventDefault();
        setActive(config.items.value.length - 1);
        break;
      case " ":
        if (!config.selectOnly) return;
      // falls through
      case "Enter":
        if (!isOpen.value) {
          // A button trigger has nowhere else for these to go; a text field does, so Enter there
          // stays available to a surrounding form.
          if (!config.selectOnly) return;
          event.preventDefault();
          open();
          return;
        }
        if (activeIndex.value < 0) return;
        event.preventDefault();
        commit(config.items.value[activeIndex.value]);
        break;
      case "Escape":
        if (!isOpen.value) return;
        event.preventDefault();
        close();
        break;
      case "Tab":
        close();
        break;
    }
  };

  // pointerdown rather than click: a click on an option would otherwise race the close, which is
  // what a blur timer is usually there to paper over. Capture phase so an ancestor that stops
  // propagation cannot leave the list open with nothing able to dismiss it.
  const onPointerDownOutside = (event: PointerEvent) => {
    if (!isOpen.value) return;
    if (root.value?.contains(event.target as Node)) return;
    if (config.panel?.value?.contains(event.target as Node)) return;
    close();
  };

  onMounted(() => document.addEventListener("pointerdown", onPointerDownOutside, true));
  onUnmounted(() => document.removeEventListener("pointerdown", onPointerDownOutside, true));

  return { root, isOpen, activeIndex, isListVisible, activeOptionId, open, close, commit, setActive, handleKeydown };
};
